"use client";

import { useEffect, useState } from "react";
import { Button } from "./ui";
import { platform, type DesignSession, type DesignHostingPortal } from "./api";
import styles from "./ManagedEntry.module.css";

export function HostingBilling({
  session,
  onUpdated,
}: {
  session: DesignSession;
  onUpdated?: () => Promise<void>;
}) {
  const [hostingList, setHostingList] = useState(session.hosting ?? []);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => setHostingList(session.hosting ?? []), [session.hosting]);
  async function refresh(id: number) {
    if (busy !== null) return;
    setBusy(id);
    setRefreshing(true);
    setError("");
    setMessage("");
    try {
      const result = await platform<
        NonNullable<DesignSession["hosting"]>[number]
      >(`design/hosting/${id}/refresh/`, {
        method: "POST",
        body: {},
        csrf: session.csrf_token,
      });
      setHostingList((items) =>
        items.map((item) => (item.id === result.id ? result : item)),
      );
      await onUpdated?.();
      setMessage("Billing status updated");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setRefreshing(false);
      setBusy(null);
    }
  }
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState("");
  if (!session.hosting?.length) return null;
  async function manage(id: number) {
    setBusy(id);
    setError("");
    try {
      const result = await platform<DesignHostingPortal>(
        `design/hosting/${id}/portal/`,
        {
          method: "POST",
          body: {},
          csrf: session.csrf_token,
        },
      );
      const destination = new URL(result.url);
      if (
        destination.protocol !== "https:" ||
        destination.hostname !== "billing.stripe.com"
      )
        throw new Error(
          "The billing page is unavailable. Please contact Intelliminds.",
        );
      window.location.assign(destination.href);
    } catch (cause) {
      setError((cause as Error).message);
      setBusy(null);
    }
  }
  return (
    <section aria-label="Hosting billing">
      {hostingList.map((hosting) => (
        <article className={styles.card} key={hosting.id}>
          <h2>{hosting.website_name} hosting</h2>
          <p>Billing: {hosting.status.replaceAll("_", " ")}</p>
          {!hosting.editing_enabled && (
            <p>
              Editing is paused. Resolve hosting billing or contact Intelliminds
              to restore access.
            </p>
          )}
          {hosting.hosting_suspended && (
            <p>Public hosting is paused. Contact Intelliminds to resume it.</p>
          )}
          <div className={styles.billingActions}>
            <Button
              disabled={busy !== null}
              onClick={() => void manage(hosting.id)}
            >
              {busy === hosting.id && !refreshing
                ? "Opening billing…"
                : "Manage hosting billing"}
            </Button>
            <Button
              variant="secondary"
              disabled={busy !== null}
              onClick={() => void refresh(hosting.id)}
            >
              {busy === hosting.id && refreshing
                ? "Refreshing…"
                : "Refresh billing status"}
            </Button>
          </div>
        </article>
      ))}
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
